/**
 * `fetch` for the server-side Supabase clients: one retry of an idempotent request
 * (GET / HEAD) that failed at the network layer, and undici's reason kept in the message.
 *
 * Why: a warm Vercel instance can be frozen between requests while Supabase closes its
 * pooled keep-alive sockets; the next request reuses a dead socket and undici rejects
 * with a bare `TypeError: fetch failed` (PLAYPULSE-12…16). A fresh attempt opens a new
 * socket. Writes and RPCs (POST / PATCH / PUT / DELETE) are never retried — the first
 * attempt may have reached the database.
 *
 * supabase-js maps a rejected fetch to `error.message = "TypeError: <message>"` and drops
 * `cause`, so the cause code (ECONNRESET, UND_ERR_SOCKET, UND_ERR_CONNECT_TIMEOUT…) is
 * folded into the message: `fetch failed (ECONNRESET: read ECONNRESET)`.
 */

const RETRYABLE_METHODS = new Set(['GET', 'HEAD']);

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
  return method.toUpperCase();
}

/**
 * undici's network-layer failure is exactly `TypeError('fetch failed')` with the reason on
 * `cause`. Other TypeErrors (invalid URL, bad header value) are deterministic and not
 * retried; an abort is a DOMException.
 */
function isNetworkFailure(e: unknown): e is TypeError {
  return e instanceof TypeError && e.message === 'fetch failed';
}

function withCause(e: unknown): unknown {
  if (!isNetworkFailure(e)) return e;
  const cause = e.cause as { code?: unknown; message?: unknown } | undefined;
  if (!cause) return e;
  const code = typeof cause.code === 'string' ? cause.code : null;
  const message = typeof cause.message === 'string' ? cause.message : null;
  const reason = [code, message].filter(Boolean).join(': ');
  if (!reason) return e;
  const wrapped = new TypeError(`${e.message} (${reason})`, { cause: e.cause });
  // Keep undici's frames: postgrest-js copies the stack into `error.details`.
  if (e.stack) wrapped.stack = e.stack.replace(e.message, wrapped.message);
  return wrapped;
}

export const retryingFetch: typeof fetch = async (input, init) => {
  const callerSignal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
  try {
    return await fetch(input, init);
  } catch (e) {
    if (!isNetworkFailure(e) || !RETRYABLE_METHODS.has(methodOf(input, init))) throw withCause(e);
    if (callerSignal?.aborted) throw withCause(e);
  }
  try {
    // Inside a React render Next's patched fetch dedupes GET/HEAD per URL and memoises
    // the REJECTED promise too (next/dist/server/lib/dedupe-fetch.js), so an identical
    // retry would get the same failure back without touching the network. A signal is
    // its opt-out; a caller's own signal already bypasses it. (The retried result is not
    // memoised either, so a duplicate read later in the render makes its own retry.)
    return await fetch(input, { ...init, signal: callerSignal ?? new AbortController().signal });
  } catch (e) {
    throw withCause(e);
  }
};
